/**
 * TaxonomyOptionEditor component - Edit a single taxonomy option
 * Handles option label editing with validation
 */

import { GripVertical, X } from 'lucide-react';
import type { HTMLAttributes } from 'react';

import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import type { TaxonomyOption } from '@/types/taxonomy';

export interface TaxonomyOptionEditorProps {
  option: TaxonomyOption;
  onChange: (option: TaxonomyOption) => void;
  onRemove: () => void;
  isLoading?: boolean;
  error?: string;
  isDragging?: boolean;
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
}

export function TaxonomyOptionEditor({
  option,
  onChange,
  onRemove,
  isLoading = false,
  error,
  isDragging = false,
  dragHandleProps,
}: TaxonomyOptionEditorProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 p-2 rounded-md border border-border-subtle bg-surface-subtle',
        isDragging && 'opacity-50',
        error && 'border-danger'
      )}
    >
      <div
        className="cursor-grab text-muted hover:text-secondary touch-none"
        {...dragHandleProps}
      >
        <GripVertical className="h-4 w-4" />
      </div>

      <Input
        value={option.label}
        onChange={(e) => onChange({ ...option, label: e.target.value })}
        placeholder="Option label"
        disabled={isLoading}
        error={!!error}
        className="flex-1"
      />

      <button
        onClick={onRemove}
        className="p-1 rounded text-muted hover:text-danger hover:bg-danger/10 transition-colors"
        disabled={isLoading}
        aria-label={`Remove option ${option.label}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
